import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Gift, Lock, MapPin, Tag, Truck } from 'lucide-react';
import { fetchDeliveryEstimate, orderKeys, placeOrder } from '@/api/orders';
import { useCart } from '@/providers/CartProvider';
import { useAuth } from '@/providers/AuthProvider';
import { usePageMetadata } from '@/hooks/usePageMetadata';
import { useFormErrors } from '@/hooks/useFormErrors';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { EmptyState } from '@/components/system/EmptyState';
import { ApiException } from '@/types/api';
import { rulesFor } from '@/lib/addressRules';
import { useLocalPrice } from '@/hooks/useCurrency';
import { usePlatform } from '@/providers/PlatformProvider';
import { localDate, validateCheckout } from '@/lib/checkoutValidation';
import { usePostcodeLookup } from '@/hooks/usePostcodeLookup';
import type { AddressInput } from '@/types/order';
import { accountKeys, createAddress, fetchAddresses } from '@/api/account';
import type { Address } from '@/types/account';
import { formatAddressLines } from '@/lib/formatAddress';
import { rememberCheckoutToken } from '@/lib/checkoutToken';
import { PageHeader } from '@/components/layout/PageHeader';

/**
 * Checkout — BUILD-PLAN §24, §34, §45, §41.
 *
 * A single page with clear sections rather than a multi-step wizard. Each
 * extra step is a place to abandon, and for a basket of a few considered
 * pieces there is nothing to hide behind pagination.
 *
 * No total is ever computed here. Delivery options and tax come from
 * /cart/estimate, and the authoritative figures are recalculated again when
 * the order is placed — this page only ever displays what the server said
 * (§13/§29/§33).
 */

/**
 * Field limits, mirroring PlaceOrderRequest one for one.
 *
 * The server is still the authority — these exist so the customer meets the
 * limit while typing rather than after submitting, and so the browser's own
 * validation has something to work with. If a rule changes there, change it
 * here; a client limit that is *looser* than the server's is the failure mode
 * to avoid, because it invites input that will be rejected.
 */
const LIMITS = {
  email: 255,
  phone: 32,
  firstName: 80,
  lastName: 80,
  line1: 180,
  line2: 180,
  city: 120,
  region: 120,
  postcode: 32,
  giftRecipient: 120,
  giftMessage: 500,
  note: 500,
} as const;

const GIFT_MESSAGE_LIMIT = LIMITS.giftMessage;
const NOTE_LIMIT = LIMITS.note;

export function CheckoutPage() {
  const { cart, isLoading: cartLoading, refresh: refreshCart } = useCart();
  const { user } = useAuth();
  const { countries } = usePlatform();
  const navigate = useNavigate();
  const { fieldErrors, formError, handleError, reset } = useFormErrors();

  const [submitting, setSubmitting] = useState(false);

  /*
   * Which fields the customer has finished with. Validation messages are held
   * back until a field is in here (or until submit marks everything touched),
   * so the form does not open covered in red before anything has been typed.
   */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  /*
   | Details, then review — §24.
   |
   | Placing an order used to happen straight from the form, so the first time
   | a customer saw what they had actually bought and where it was going was
   | AFTER it was committed. A review step costs one click and is the last
   | point at which a wrong address is free to fix.
   */
  const [stage, setStage] = useState<'details' | 'review'>('details');

  /*
   | Consent to keep an account — §16, §24.
   |
   | Defaults to FALSE. A pre-ticked box is not consent under GDPR Art. 7, and
   | an account someone did not ask for is a data-protection liability rather
   | than a conversion win.
   */
  const [createAccount, setCreateAccount] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [method, setMethod] = useState<string>('');
  const [isGift, setIsGift] = useState(false);
  const [giftRecipient, setGiftRecipient] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [hidePrices, setHidePrices] = useState(true);
  const [scheduledDate, setScheduledDate] = useState('');
  const [note, setNote] = useState('');
  const [acceptsTerms, setAcceptsTerms] = useState(false);

  /*
   * Two pieces of state, not one. `codeInput` is what the customer is typing;
   * `appliedCode` is what has been sent to the server. Keeping them separate
   * means the quote is not re-fetched on every keystroke, and a half-typed
   * code is never reported as invalid.
   */
  const [codeInput, setCodeInput] = useState('');
  const [appliedCode, setAppliedCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);

  /*
   * The default destination is the first market we ship to, not a hardcoded
   * "GB". The registry is server-owned and can change without a deploy; a
   * literal here would eventually name a country the shop no longer serves.
   */
  const [address, setAddress] = useState<AddressInput>({
    first_name: '',
    last_name: '',
    line_1: '',
    line_2: '',
    city: '',
    region: '',
    postcode: '',
    country_code: 'GB',
  });

  /*
   | The customer's saved addresses — §16, §24.
   |
   | Only fetched when signed in: the endpoint is authenticated, and asking as
   | a guest is a guaranteed 401 on every checkout load.
   */
  const { data: savedAddresses = [] } = useQuery({
    queryKey: accountKeys.addresses,
    queryFn: fetchAddresses,
    enabled: Boolean(user),
    staleTime: 5 * 60 * 1000,
  });

  /** Which saved address is in the form, or 'new' for one typed by hand. */
  const [selectedAddressId, setSelectedAddressId] = useState<number | 'new' | null>(null);

  /** Offer to keep a hand-typed address, but never save one unasked. */
  const [saveNewAddress, setSaveNewAddress] = useState(false);

  usePageMetadata({ title: 'Checkout', description: 'Complete your Glanz Luxe order.' });

  /*
   | Fill from the default saved address, ONCE.
   |
   | Guarded on `selectedAddressId === null` so it only ever runs before the
   | customer has chosen anything. Without that guard, editing a field would
   | be undone the moment the query refetched — the form would keep snapping
   | back to the saved copy and the customer would have no idea why.
   */
  useEffect(() => {
    if (selectedAddressId !== null || savedAddresses.length === 0) return;

    const preferred = savedAddresses.find((entry) => entry.isDefault) ?? savedAddresses[0];

    if (preferred) applySavedAddress(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses, selectedAddressId]);

  function applySavedAddress(saved: Address) {
    setSelectedAddressId(saved.id);
    setSaveNewAddress(false);
    setAddress({
      first_name: saved.firstName,
      last_name: saved.lastName,
      line_1: saved.line1,
      line_2: saved.line2 ?? '',
      city: saved.city,
      region: saved.region ?? '',
      postcode: saved.postcode ?? '',
      country_code: saved.countryCode,
    });
  }

  // Prefill from the signed-in customer, so they are not retyping what we know.
  useEffect(() => {
    if (user?.email) setEmail(user.email);
    if (user?.phone) setPhone(user.phone);
  }, [user]);

  /*
   * Re-quoted whenever the destination OR the method changes: the total
   * depends on both, and it is the server that computes it. `placeholderData`
   * keeps the previous quote on screen during the refetch so the summary does
   * not blank out each time a method is picked.
   */
  const { data: estimate, isPending: estimatePending } = useQuery({
    queryKey: orderKeys.estimate(address.country_code, method, appliedCode),
    queryFn: () =>
      fetchDeliveryEstimate(address.country_code, method || undefined, appliedCode || undefined),
    enabled: Boolean(address.country_code) && (cart?.itemCount ?? 0) > 0,
    placeholderData: (previous) => previous,
  });

  /*
   * Choose a default only when there is no valid selection — on first quote,
   * or when a destination change removes the method that was picked.
   *
   * It must NOT reset on every new estimate. The estimate is re-quoted when
   * the method changes, so an unconditional reset would immediately undo the
   * customer's choice and snap the selection back to the cheapest option.
   */
  useEffect(() => {
    const methods = estimate?.deliveryMethods;
    const fallback = methods?.[0];

    if (!fallback) return;

    const stillOffered = methods.some((option) => option.code === method);

    if (!stillOffered) setMethod(fallback.code);
  }, [estimate, method]);

  /*
   * Named-day delivery requires a date. Derived rather than stored, so it can
   * never fall out of step with the selected method — and the server enforces
   * the same rule, so this is guidance, not the control.
   */
  const requiresDate = method === 'named_day';

  /** Address conventions for the chosen market — labels, formats, options. */
  const rules = rulesFor(address.country_code, countries);

  /*
   * Verified against a real postcode authority, not just a regex. A format
   * check only proves the entry is postcode-shaped — "SW7 9ZZ" passes every
   * pattern and is not a real place. The server repeats this check when the
   * order is placed, so this is early warning rather than the control (§43).
   */
  const postcodeCheck = usePostcodeLookup(
    address.country_code,
    address.postcode ?? '',
    address.city ?? '',
  );

  /* Recomputed every render: cheap, and never stale against the inputs. */
  const clientErrors = validateCheckout(
    { email, phone, address, method, scheduledDate, acceptsTerms },
    countries,
  );

  const isValid = Object.keys(clientErrors).length === 0 && !postcodeCheck.isWrong;

  /**
   * The message to show for a field.
   *
   * A server error always wins — it knows things the client cannot, and it is
   * the authority. A client message appears only once the field has been
   * visited or the customer has tried to submit.
   */
  const errorFor = (key: string): string | undefined =>
    fieldErrors[key] ?? (touched[key] || submitAttempted ? clientErrors[key] : undefined);

  const markTouched = (key: string) => setTouched((current) => ({ ...current, [key]: true }));

  /*
   * An indicative total in the destination's own currency (§56). Null
   * whenever there is no rate, the feed was unreachable, or the destination
   * already uses the base currency — a missing conversion is a small loss of
   * comfort, a wrong one is a misquote.
   */
  const localTotal = useLocalPrice(
    estimate?.estimatedTotal.minor,
    rules.currency,
  );

  /* The same courtesy for the free-delivery threshold. */
  const localToFreeDelivery = useLocalPrice(
    estimate?.amountToFreeDelivery?.minor,
    rules.currency,
  );

  /**
   * Earliest date we can commit to: tomorrow, matching the server rule.
   * Computed in the customer's own timezone — see localDate().
   */
  const earliestDeliveryDate = localDate(1);

  /**
   * Send the typed code to the server to be checked.
   *
   * The server decides. Nothing here inspects the code or guesses at a
   * saving — the request asks whether a rule applies, and a rejection comes
   * back as a message the customer can act on (§29, §64).
   */
  async function applyCode() {
    const code = codeInput.trim();

    if (!code) return;

    setCodeError(null);

    try {
      await fetchDeliveryEstimate(address.country_code, method || undefined, code);
      setAppliedCode(code);
    } catch (error) {
      /*
       | A rejected code must not clear a discount that was already working —
       | mistyping a second code should not cost the customer the first.
       */
      setCodeError(
        error instanceof ApiException
          ? (error.errors?.['discount_code']?.[0] ?? error.message)
          : 'That code could not be applied.',
      );
    }
  }

  function removeCode() {
    setAppliedCode('');
    setCodeInput('');
    setCodeError(null);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitAttempted(true);
    reset();

    /*
     | Stop here when the client already knows the form is wrong, and put the
     | customer in the first bad field rather than making them hunt for it.
     */
    if (!isValid) {
      const firstKey = Object.keys(clientErrors)[0];
      const target = firstKey
        ? document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`)
        : null;

      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      target?.focus({ preventScroll: true });

      return;
    }

    /*
     | First valid submit goes to REVIEW, not to the payment provider.
     |
     | The form stays mounted behind it (hidden, not unmounted) so going back
     | to change something finds every field exactly as it was left.
     */
    if (stage === 'details') {
      setStage('review');
      window.scrollTo({ top: 0, behavior: 'smooth' });

      return;
    }

    setSubmitting(true);

    try {
      /*
       | Saved BEFORE the order, and never allowed to break it.
       |
       | The address book is a convenience; the order is the thing the
       | customer came for. If saving fails — a validation rule the checkout
       | form is laxer about, say — the purchase still goes through and the
       | customer is not told about a bookkeeping problem they cannot act on.
       */
      if (user && selectedAddressId === 'new' && saveNewAddress) {
        try {
          await createAddress({
            first_name: address.first_name,
            last_name: address.last_name,
            line_1: address.line_1,
            line_2: address.line_2 ?? null,
            city: address.city,
            region: address.region ?? null,
            postcode: address.postcode ?? null,
            country_code: address.country_code,
          });
        } catch {
          // Deliberately swallowed — see above.
        }
      }

      const order = await placeOrder({
        email,
        ...(phone ? { phone } : {}),
        shipping_address: address,
        shipping_method: method,
        is_gift: isGift,
        ...(isGift && giftRecipient ? { gift_recipient_name: giftRecipient } : {}),
        ...(isGift && giftMessage ? { gift_message: giftMessage } : {}),
        ...(isGift ? { hide_prices_from_recipient: hidePrices } : {}),
        ...(scheduledDate ? { scheduled_delivery_date: scheduledDate } : {}),
        ...(note ? { customer_note: note } : {}),
        ...(appliedCode ? { discount_code: appliedCode } : {}),
        accepts_terms: acceptsTerms,
        // Only sent for a guest — a signed-in buyer already has an account.
        ...(!user && createAccount ? { create_account: true } : {}),
      });

      /*
       | The server empties the cart as part of placing the order, so the
       | client's copy is now wrong. Without this the header badge and drawer
       | keep advertising items the customer has just bought.
       */
      await refreshCart();

      /*
       | Survive the trip to the gateway.
       |
       | Router state is gone the moment the customer returns from
       | checkout.stripe.com — that is a fresh document from another origin.
       | Without this the confirmation page cannot authorise the "did it
       | succeed?" call, and a paid order never confirms itself.
       */
      rememberCheckoutToken(order.orderNumber, order.checkoutToken);

      navigate(`/orders/${order.orderNumber}/confirmation`, {
        replace: true,
        state: { order },
      });
    } catch (error) {
      handleError(error);

      if (error instanceof ApiException) {
        // Scroll to the message rather than leaving it below the fold on a
        // long form (§41).
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (!cartLoading && (cart?.itemCount ?? 0) === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <EmptyState
          icon={Gift}
          title="There is nothing to check out"
          description="Your cart is empty."
          action={<ButtonLink to="/shop">Explore the Collection</ButtonLink>}
        />
      </div>
    );
  }

  const field = (key: keyof AddressInput) => ({
    value: address[key] ?? '',
    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
      setAddress((current) => ({ ...current, [key]: event.target.value })),
    onBlur: () => markTouched(`shipping_address.${key}`),
    error: errorFor(`shipping_address.${key}`),
    'data-field': `shipping_address.${key}`,
  });

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-6 lg:px-8 lg:py-16">
      <PageHeader eyebrow="Almost there" title="Checkout" className="mb-10" />

      <form
        onSubmit={handleSubmit}
        noValidate
        /*
         | `[&>*]:min-w-0` is what stops the page scrolling sideways.
         |
         | A grid item's default `min-width: auto` refuses to shrink below its
         | content's intrinsic width, so one wide descendant inside the form
         | column pushed the column to 386px inside a 350px content box — and
         | the whole checkout scrolled horizontally on a 390px phone. Nothing
         | looked wrong in any single component; the overflow only exists at
         | the grid boundary.
         */
        className="grid gap-12 [&>*]:min-w-0 lg:grid-cols-[1fr_22rem] lg:gap-16"
      >
        <div className={stage === 'review' ? 'hidden' : 'flex flex-col gap-10'}>
          {formError && <Alert tone="error">{formError}</Alert>}

          <Section index={1} title="Contact">
            <div className="grid gap-5 sm:grid-cols-2">
              <Input
                label="Email address"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() => markTouched('email')}
                error={errorFor('email')}
                data-field="email"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={LIMITS.email}
                required
              />
              <Input
                label="Phone"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                onBlur={() => markTouched('phone')}
                error={errorFor('phone')}
                data-field="phone"
                autoComplete="tel"
                inputMode="tel"
                maxLength={LIMITS.phone}
                /* Mirrors the server regex: digits, spaces and + ( ) - only. */
                pattern="[0-9+()\-\s]+"
                /* The dial code alone. Appending a specimen number to it
                   produced nonsense like "+1 20 7946 0958" — a US code on a
                   London number — for every market except the UK. */
                hint={
                  rules.dialCode
                    ? `For delivery updates. Include your country code (${rules.dialCode}).`
                    : 'For delivery updates.'
                }
              />
            </div>

            {!user && (
              <p className="mt-4 text-xs text-muted">
                Checking out as a guest.{' '}
                <Link to="/login?returnTo=%2Fcheckout" className="text-accent underline underline-offset-4">
                  Sign in
                </Link>{' '}
                to use your saved details.
              </p>
            )}
          </Section>

          <Section index={2} title={isGift ? "Recipient's address" : 'Delivery address'}>
            {/*
              Saved addresses, chosen rather than retyped — §16, §24.
              ----------------------------------------------------------------
              Only ever fills the form. Nothing here edits or overwrites a
              saved address: picking one copies it into the fields, and any
              change the customer then makes belongs to THIS order until they
              explicitly ask to keep it. Silently updating the address book
              because someone corrected a postcode for one delivery would
              rewrite history they never agreed to.
            */}
            {savedAddresses.length > 0 && (
              <fieldset className="mb-6 border-b border-line pb-6">
                <legend className="mb-3 text-[0.6875rem] uppercase tracking-[0.2em] text-ink">
                  Deliver to
                </legend>

                <div className="flex flex-wrap gap-2">
                  {savedAddresses.map((saved) => {
                    const active = selectedAddressId === saved.id;

                    return (
                      <button
                        key={saved.id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => applySavedAddress(saved)}
                        className={[
                          'min-h-11 rounded-full border px-4 text-xs tracking-wide transition-colors',
                          active
                            ? 'border-accent bg-accent text-white'
                            : 'border-line bg-surface text-ink hover:border-accent hover:text-accent',
                        ].join(' ')}
                      >
                        {saved.label || `${saved.firstName} ${saved.lastName}`}
                        <span className={active ? 'text-white/70' : 'text-muted'}>
                          {' '}
                          · {saved.postcode || saved.city}
                        </span>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    aria-pressed={selectedAddressId === 'new'}
                    onClick={() => {
                      setSelectedAddressId('new');
                      setAddress({
                        first_name: '',
                        last_name: '',
                        line_1: '',
                        line_2: '',
                        city: '',
                        region: '',
                        postcode: '',
                        country_code: address.country_code,
                      });
                    }}
                    className={[
                      'min-h-11 rounded-full border px-4 text-xs tracking-wide transition-colors',
                      selectedAddressId === 'new'
                        ? 'border-accent bg-accent text-white'
                        : 'border-line bg-surface text-ink hover:border-accent hover:text-accent',
                    ].join(' ')}
                  >
                    Use a different address
                  </button>
                </div>

                {/* Offered only for an address that is not already saved. */}
                {selectedAddressId === 'new' && (
                  <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={saveNewAddress}
                      onChange={(event) => setSaveNewAddress(event.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
                    />
                    <span>
                      Save this to my address book
                      <span className="block text-xs text-muted">
                        Added as a new address — nothing you have saved is changed.
                      </span>
                    </span>
                  </label>
                )}
              </fieldset>
            )}

            {isGift && (
              <p className="mb-5 text-xs text-muted">
                We will send this straight to them — enter their address, not yours.
              </p>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <Input
                label="First name"
                {...field('first_name')}
                autoComplete="given-name"
                maxLength={LIMITS.firstName}
                required
              />
              <Input
                label="Last name"
                {...field('last_name')}
                autoComplete="family-name"
                maxLength={LIMITS.lastName}
                required
              />
            </div>

            <div className="mt-5">
              <Select
                id="country"
                label="Country"
                value={address.country_code}
                onChange={(value) =>
                  /*
                   | Changing country changes which fields apply, so the
                   | region and postcode are cleared rather than carried over.
                   | A US state left in a French address would be submitted
                   | and stored as nonsense.
                   */
                  setAddress((current) => ({
                    ...current,
                    country_code: value,
                    region: '',
                    postcode: '',
                  }))
                }
                error={errorFor('shipping_address.country_code')}
                required
                /* Straight from the registry, so a country can never be
                   offered here that checkout would then refuse (§86). */
                options={countries.map((country) => ({
                  value: country.code,
                  label: country.name,
                }))}
                data-field="shipping_address.country_code"
              />
            </div>
            
            <div className="mt-5 grid gap-5">
              <Input
                label="Address line 1"
                {...field('line_1')}
                autoComplete="address-line1"
                maxLength={LIMITS.line1}
                required
              />
              <Input
                label="Address line 2"
                {...field('line_2')}
                autoComplete="address-line2"
                maxLength={LIMITS.line2}
              />
            </div>

            {/*
              Labels, requirements and formats all follow the destination.
              "ZIP code" is meaningless in the UK, France has no region to
              give, and the UAE has no postal codes at all — a single fixed
              row would be wrong in most of these markets (§43).
            */}
            <div className="mt-5 grid gap-5 sm:grid-cols-3">
              <Input
                label="Town / City"
                {...field('city')}
                autoComplete="address-level2"
                maxLength={LIMITS.city}
                required
              />

              {rules.regionLabel &&
                (rules.regionOptions ? (
                  /* A fixed list where one exists — free text invites typos
                     in exactly the field a courier cannot guess. */
                  <Select
                    label={rules.regionLabel}
                    value={address.region ?? ''}
                    onChange={(value) => setAddress((current) => ({ ...current, region: value }))}
                    onBlur={() => markTouched('shipping_address.region')}
                    error={errorFor('shipping_address.region')}
                    required={rules.regionRequired}
                    placeholder={`Select a ${rules.regionLabel.toLowerCase()}`}
                    /* Already in {value,label} shape from the registry. */
                    options={rules.regionOptions}
                    data-field="shipping_address.region"
                  />
                ) : (
                  <Input
                    label={rules.regionLabel}
                    {...field('region')}
                    autoComplete="address-level1"
                    maxLength={LIMITS.region}
                    required={rules.regionRequired}
                  />
                ))}

              {rules.postcodeLabel && (
                <Input
                  label={rules.postcodeLabel}
                  {...field('postcode')}
                  onChange={(event) =>
                    setAddress((current) => ({
                      ...current,
                      postcode: rules.uppercasePostcode
                        ? event.target.value.toUpperCase()
                        : event.target.value,
                    }))
                  }
                  autoComplete="postal-code"
                  maxLength={LIMITS.postcode}
                  required={rules.postcodeRequired}
                  {...(rules.postcodeExample ? { placeholder: rules.postcodeExample } : {})}
                  /*
                    A verified failure outranks the format message: knowing the
                    code does not exist is strictly more useful than knowing it
                    is the wrong shape.
                  */
                  {...(postcodeCheck.isWrong
                    ? { error: 'That postcode does not exist. Please check it.' }
                    : {})}
                  {...(postcodeCheck.isChecking ? { hint: 'Checking…' } : {})}
                />
              )}
            </div>

            {/*
              Offered, never imposed. A postcode covers several valid place
              names, so this is a "did you mean" with a one-tap fix rather
              than an error that blocks a real address (§41, §86).
            */}
            {postcodeCheck.suggestedCity && (
              <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={1.5} aria-hidden="true" />
                That postcode is in{' '}
                <span className="text-ink">{postcodeCheck.suggestedCity}</span>.
                <button
                  type="button"
                  onClick={() =>
                    setAddress((current) => ({
                      ...current,
                      city: postcodeCheck.suggestedCity ?? current.city,
                      /* Fill the region too when we know it and the customer
                         has not chosen one — one tap, not three. */
                      region:
                        current.region ||
                        postcodeCheck.regionCode ||
                        postcodeCheck.region ||
                        '',
                    }))
                  }
                  className="text-accent underline underline-offset-4 hover:text-ink"
                >
                  Use this
                </button>
              </p>
            )}

          </Section>

          <Section index={3} title="Delivery method">
            {estimatePending ? (
              <p className="text-sm text-muted">Finding delivery options…</p>
            ) : (estimate?.deliveryMethods.length ?? 0) === 0 ? (
              <Alert tone="error">
                We are not able to deliver to that country yet. Please choose another, or
                contact us about your order.
              </Alert>
            ) : (
              <div role="radiogroup" aria-label="Delivery method" className="flex flex-col gap-3">
                {estimate?.deliveryMethods.map((option) => (
                  <label
                    key={option.code}
                    className={`flex cursor-pointer items-center justify-between gap-4 border px-5 py-4 transition-colors ${
                      method === option.code ? 'border-accent' : 'border-line hover:border-ink'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="shipping_method"
                        value={option.code}
                        checked={method === option.code}
                        onChange={() => setMethod(option.code)}
                        className="h-4 w-4 accent-accent"
                      />
                      <span className="flex flex-col">
                        <span className="text-sm text-ink">{option.label}</span>
                        <span className="text-xs text-muted">{option.days}</span>
                      </span>
                    </span>

                    <span className="text-sm text-ink">
                      {option.isFree ? 'Free' : option.price.formatted}
                    </span>
                  </label>
                ))}
              </div>
            )}

            {/*
              A named-day service promises "choose your date", so the date
              field belongs here, the moment that option is chosen — not in a
              separate optional section further down. The server requires it
              for this method too, so the two agree (§86).
            */}
            {requiresDate && (
              <div className="mt-5 border-l border-accent/40 pl-5">
                <Input
                  label="Choose your delivery date"
                  type="date"
                  value={scheduledDate}
                  onChange={(event) => setScheduledDate(event.target.value)}
                  onBlur={() => markTouched('scheduled_delivery_date')}
                  error={errorFor('scheduled_delivery_date')}
                  data-field="scheduled_delivery_date"
                  min={earliestDeliveryDate}
                  required
                  hint="We will deliver on this date."
                />
              </div>
            )}

            {/*
              Thresholds the basket has not reached — free delivery, and any
              §64 bulk tier — each with a way to act on it.
              ------------------------------------------------------------------
              "Add £30.00 more for free standard delivery" on its own is a
              statement, not an offer: it names a gap and leaves the customer
              with nothing to do about it. Each row now carries the reward AND
              a route back to the shop, and the amount is shown in the money
              the customer will actually be charged in (§56, §86).
            */}
            {(estimate?.amountToFreeDelivery || (estimate?.nearMisses?.length ?? 0) > 0) && (
              <ul className="mt-4 flex flex-col gap-2">
                {estimate?.amountToFreeDelivery && (
                  <li className="flex flex-wrap items-center gap-x-2 gap-y-1 border border-accent/30 bg-accent/5 px-3.5 py-2.5 text-xs text-muted">
                    <Truck className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={1.5} aria-hidden="true" />
                    <span>
                      Add <span className="text-ink">{estimate.amountToFreeDelivery.formatted}</span>
                      {localToFreeDelivery ? ` (${localToFreeDelivery.formatted})` : ''} more for{' '}
                      <span className="text-ink">free standard delivery</span>.
                    </span>
                    <Link
                      to="/shop"
                      className="text-accent underline underline-offset-4 hover:text-ink"
                    >
                      Add something
                    </Link>
                  </li>
                )}

                {estimate?.nearMisses?.map((miss) => (
                  <li
                    key={miss.name}
                    className="flex flex-wrap items-center gap-x-2 gap-y-1 border border-accent/30 bg-accent/5 px-3.5 py-2.5 text-xs text-muted"
                  >
                    <Tag className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={1.5} aria-hidden="true" />
                    <span>
                      {miss.unitsNeeded !== null ? (
                        <>
                          Add{' '}
                          <span className="text-ink">
                            {miss.unitsNeeded} more {miss.unitsNeeded === 1 ? 'item' : 'items'}
                          </span>{' '}
                          for <span className="text-ink">{miss.reward}</span>.
                        </>
                      ) : (
                        <>
                          Spend <span className="text-ink">{miss.subtotalNeeded?.formatted}</span>{' '}
                          more for <span className="text-ink">{miss.reward}</span>.
                        </>
                      )}
                    </span>
                    <Link
                      to="/shop"
                      className="text-accent underline underline-offset-4 hover:text-ink"
                    >
                      Browse
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section index={4} title="Is this a gift?">
            <label className="flex items-center gap-2.5 text-sm text-ink">
              <input
                type="checkbox"
                checked={isGift}
                onChange={(event) => setIsGift(event.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              Yes — this is being sent as a gift
            </label>

            {isGift && (
              <div className="mt-5 flex flex-col gap-5 border-l border-accent/40 pl-5">
                <Input
                  label="Recipient's name"
                  value={giftRecipient}
                  onChange={(event) => setGiftRecipient(event.target.value)}
                  error={errorFor('gift_recipient_name')}
                  maxLength={LIMITS.giftRecipient}
                  hint="Printed on the gift card as the name it is addressed to."
                />

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="gift-message" className="text-xs font-medium tracking-wide text-ink">
                    Gift message
                  </label>
                  <textarea
                    id="gift-message"
                    value={giftMessage}
                    onChange={(event) => setGiftMessage(event.target.value)}
                    maxLength={GIFT_MESSAGE_LIMIT}
                    rows={3}
                    placeholder={
                      giftRecipient
                        ? `Dear ${giftRecipient}, …`
                        : 'A few words to go with it…'
                    }
                    className="rounded-sm border border-line bg-surface px-3.5 py-2.5 text-sm text-ink"
                  />
                  <div className="flex justify-between gap-3 text-xs text-muted">
                    <span>Handwritten onto your gift card.</span>
                    {/* Shown as it fills, so the limit is never a surprise. */}
                    <Counter used={giftMessage.length} limit={GIFT_MESSAGE_LIMIT} />
                  </div>
                </div>

                {/* §45: the recipient should not learn what the gift cost. */}
                <label className="flex items-start gap-2.5 text-sm text-muted">
                  <input
                    type="checkbox"
                    checked={hidePrices}
                    onChange={(event) => setHidePrices(event.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-accent"
                  />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-ink">Hide prices from the recipient</span>
                    {/* The consequence, stated — otherwise this is a switch
                        with no visible effect. */}
                    <span className="text-xs">
                      {hidePrices
                        ? 'No prices will appear on the packing slip or gift card.'
                        : 'Prices will be shown on the packing slip.'}
                    </span>
                  </span>
                </label>
              </div>
            )}
          </Section>

          <Section index={5} title={requiresDate ? 'Additional notes' : 'Delivery date'}>
            {/* Hidden when the delivery method already collected a date —
                asking twice for the same thing reads as a bug. */}
            {!requiresDate && (
              <Input
                label="Deliver on (optional)"
                type="date"
                value={scheduledDate}
                onChange={(event) => setScheduledDate(event.target.value)}
                onBlur={() => markTouched('scheduled_delivery_date')}
                error={errorFor('scheduled_delivery_date')}
                data-field="scheduled_delivery_date"
                min={earliestDeliveryDate}
                hint="We will hold your order and aim to deliver on this date."
              />
            )}

            <div className="mt-6 flex flex-col gap-1.5">
              <label htmlFor="customer-note" className="text-xs font-medium tracking-wide text-ink">
                Anything else we should know?
              </label>
              <textarea
                id="customer-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={NOTE_LIMIT}
                rows={3}
                className="rounded-sm border border-line bg-surface px-3.5 py-2.5 text-sm text-ink"
              />
              <div className="flex justify-between gap-3 text-xs text-muted">
                <span>Delivery instructions, or anything about this order.</span>
                <Counter used={note.length} limit={NOTE_LIMIT} />
              </div>
            </div>
          </Section>
        </div>

        {stage === 'review' && (
          <div className="flex flex-col gap-8">
            {formError && <Alert tone="error">{formError}</Alert>}

            <ReviewPanel
              email={email}
              phone={phone}
              address={address}
              countryName={
                countries.find((entry) => entry.code === address.country_code)?.name ??
                address.country_code
              }
              methodLabel={
                estimate?.deliveryMethods?.find((option) => option.code === method)?.label ??
                method
              }
              scheduledDate={scheduledDate}
              isGift={isGift}
              giftRecipient={giftRecipient}
              giftMessage={giftMessage}
              note={note}
              onEdit={() => {
                setStage('details');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </div>
        )}

        {/* Summary — sticky so the total stays visible down a long form. */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="border border-line bg-surface p-7">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-xl text-ink">Order summary</h2>
              <Link
                to="/cart"
                className="text-xs text-accent underline underline-offset-4 hover:text-ink"
              >
                Edit cart
              </Link>
            </div>

            <ul className="mt-5 flex flex-col gap-3 border-b border-line pb-5">
              {cart?.items.map((item) => (
                <li key={item.id} className="flex justify-between gap-3 text-sm">
                  <span className="min-w-0 text-muted">
                    <span className="text-ink">{item.product.name}</span>
                    {item.variant.name && ` · ${item.variant.name}`}
                    {` × ${item.quantity}`}
                  </span>
                  <span className="shrink-0 text-ink">{item.lineTotal.formatted}</span>
                </li>
              ))}
            </ul>

            {isGift && (
              <p className="mt-4 flex items-start gap-2 border border-accent/30 bg-accent/5 px-3.5 py-2.5 text-xs leading-relaxed text-muted">
                <Gift className="mt-0.5 h-3 w-3 shrink-0 text-accent" strokeWidth={1.5} aria-hidden="true" />
                <span>
                  Sending as a gift{giftRecipient ? ` to ${giftRecipient}` : ''}
                  {hidePrices ? ', with prices hidden' : ''}.
                </span>
              </p>
            )}

            <dl className="mt-5 flex flex-col gap-2.5 text-sm">
              <Row label="Subtotal" value={cart?.totals.subtotal.formatted} />
              {estimate && (
                <>
                  {/* The server's own figure for the quoted method, so the
                      lines and the total can never disagree. */}
                  <Row
                    label="Delivery"
                    value={
                      estimate.shipping.minor === 0 ? 'Free' : estimate.shipping.formatted
                    }
                  />
                  {/* Shown only when there is a real saving — a "£0.00 off"
                      line reads as a failure rather than an absence (§86). */}
                  {estimate.discount && (
                    <div className="flex justify-between text-accent">
                      <dt className="flex items-center gap-1.5">
                        <Tag className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                        {estimate.discount.name}
                      </dt>
                      <dd>−{estimate.discount.amount.formatted}</dd>
                    </div>
                  )}

                  {/*
                    Only EXCLUSIVE tax belongs in this column.
                    ------------------------------------------------------------
                    These rows read as a sum leading to the total, so a figure
                    that is NOT part of that sum does not belong among them —
                    inclusive tax is already inside the subtotal, and listing
                    it here made the arithmetic look wrong even though it was
                    right. "(included)" is not enough: a reader adds the
                    column before they read the labels. Inclusive tax is now
                    stated under the total instead (§33, §86).
                  */}
                  {!estimate.tax.inclusive && (
                    <Row label={estimate.tax.label} value={estimate.tax.amount.formatted} />
                  )}
                </>
              )}
            </dl>

            {estimate && (
              <div className="mt-5 border-t border-line pt-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted">Total</span>
                  {/* Labelled for the browser checks. Matching the total by
                      DOM shape was brittle: adding the tax note below it made
                      the script read the VAT as the total. */}
                  <span data-summary="total" className="font-display text-2xl text-ink">
                    {estimate.estimatedTotal.formatted}
                  </span>
                </div>

                {/*
                  Inclusive tax, stated as a BREAKDOWN of the total.
                  ------------------------------------------------------------
                  It cannot sit among the rows above — those are addends that
                  sum to the total, and inclusive tax is already inside the
                  subtotal, so listing it there makes the arithmetic look
                  wrong. But a 12px grey footnote is the wrong answer in the
                  other direction: under the UK Price Marking Order and the
                  Consumer Protection Regulations the customer must be able to
                  SEE the tax they are paying, and burying it reads as a
                  charge someone is hoping they will not notice.

                  So: same size and alignment as every other summary line,
                  directly beneath the total and inside its rule, with "of
                  which" making the relationship explicit — it is part of the
                  figure above, not an addition to it. Transparent about the
                  amount, honest about the arithmetic.
                */}
                {estimate.tax.inclusive && estimate.tax.amount.minor > 0 && (
                  <div className="mt-2 flex items-baseline justify-between text-sm">
                    <span className="text-muted">of which {estimate.tax.label}</span>
                    <span data-summary="tax-included" className="text-ink">
                      {estimate.tax.amount.formatted}
                    </span>
                  </div>
                )}

                {/*
                  §56 — shown beside the real price, never instead of it.
                  The card is charged in the base currency at the bank's rate
                  on the day, so presenting this as the amount payable would
                  be a misquote. It is labelled approximate for that reason.
                */}
                {localTotal && (
                  <p className="mt-1.5 text-right text-xs text-muted">
                    {localTotal.formatted} approx. · charged in{' '}
                    {estimate.estimatedTotal.currency}
                  </p>
                )}
              </div>
            )}

            {/* -------------------------------------------------- §64 code */}
            <div className="mt-6 border-t border-line pt-5">
              {estimate?.discount?.code ? (
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2 text-accent">
                    <Tag className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                    {estimate.discount.code} applied
                  </span>
                  <button
                    type="button"
                    onClick={removeCode}
                    className="text-xs text-muted underline underline-offset-4 hover:text-ink"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Input
                      label="Discount code"
                      value={codeInput}
                      onChange={(event) => setCodeInput(event.target.value.toUpperCase())}
                      error={codeError ?? undefined}
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={40}
                      /* Enter applies the code rather than submitting the
                         order — a customer pressing it in this field means
                         "check my code", not "place my order". */
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void applyCode();
                        }
                      }}
                    />
                  </div>

                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void applyCode()}
                    disabled={!codeInput.trim()}
                    className="mb-[1px]"
                  >
                    Apply
                  </Button>
                </div>
              )}
            </div>

            <label className="mt-7 flex items-start gap-2.5 text-xs leading-relaxed text-muted">
              <input
                type="checkbox"
                checked={acceptsTerms}
                onChange={(event) => setAcceptsTerms(event.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
              />
              <span>
                I agree to the{' '}
                <Link to="/policies/terms" className="text-accent underline underline-offset-4">
                  Terms &amp; Conditions
                </Link>
                .
              </span>
            </label>

            {/*
              Offered only to guests, and stating exactly what happens.
              ------------------------------------------------------------
              "Create an account" alone leaves the customer wondering what
              password they now have. Naming the mechanism — a link, not a
              password — is both the honest answer and the reassuring one.
            */}
            {!user && (
              <label className="mt-4 flex items-start gap-2.5 text-xs leading-relaxed text-muted">
                <input
                  type="checkbox"
                  checked={createAccount}
                  onChange={(event) => setCreateAccount(event.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
                />
                <span>
                  Save my details as a Glanz Luxe account.
                  <span className="mt-0.5 block text-[0.6875rem] text-muted">
                    We will email you a link to choose a password — never a password itself.
                    Your order will already be saved to it.
                  </span>
                </span>
              </label>
            )}

            {errorFor('accepts_terms') && (
              <p role="alert" className="mt-2 text-xs text-danger" data-field="accepts_terms">
                {errorFor('accepts_terms')}
              </p>
            )}

            <div className="mt-6">
              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={submitting}
                /*
                  Disabled only for blockers whose reason is visible right
                  next to the button or on the field itself — a missing
                  delivery date, or a postcode we have verified does not
                  exist. Everything else stays clickable so submitting can
                  reveal what is wrong, rather than leaving the customer with
                  a dead button and no explanation (§86).
                */
                disabled={
                  !method || (requiresDate && !scheduledDate) || postcodeCheck.isWrong
                }
              >
                {stage === 'details' ? 'Review your order' : 'Place order & pay'}
              </Button>
            </div>

            {requiresDate && !scheduledDate && (
              <p className="mt-3 text-xs text-danger">
                Please choose a delivery date for named-day delivery.
              </p>
            )}

            {/*
              What happens next, stated accurately.
              ------------------------------------------------------------------
              This used to read "card payment is not yet available online",
              which was true when it was written and false the moment a
              gateway went live. Copy that asserts a system state goes stale
              silently — it contradicted a working Pay button one screen
              later, and only the customer could see the contradiction.
              Saying what the next step IS carries no such expiry (§86).
            */}
            <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted">
              <Lock className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={1.5} aria-hidden="true" />
              You will choose how to pay on the next step. Your card details never reach our
              servers.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}

/**
 * A numbered step in the checkout.
 *
 * The numerals are the site's editorial mark — the same two-digit index the
 * homepage sections and page headers carry — but on a long form they earn
 * their keep twice over: they tell a customer how far through they are and
 * how much is left, which is the single thing a one-page checkout otherwise
 * fails to communicate. A form that looks endless is a form people abandon.
 */
function Section({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-5 flex items-baseline gap-3 border-b border-line pb-3">
        <span aria-hidden="true" className="font-display text-sm text-accent/70">
          {String(index).padStart(2, '0')}
        </span>

        <span className="h-px w-6 self-center bg-accent/50" aria-hidden="true" />

        <h2 className="font-display text-xl text-ink">{title}</h2>
      </div>

      {children}
    </section>
  );
}

/**
 * Live character count. Silent only while there is room to spare — it earns
 * attention by turning amber near the limit rather than nagging from zero.
 */
function Counter({ used, limit }: { used: number; limit: number }) {
  if (used < limit * 0.8) return null;

  return (
    <span className={used >= limit ? 'text-danger' : 'text-accent'}>
      {used} / {limit}
    </span>
  );
}

function Row({ label, value }: { label: string; value?: string | undefined }) {
  return (
    <div className="flex justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="text-ink">{value ?? '—'}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------------ */

/**
 * The review step — §24, §41.
 *
 * Everything the customer is about to commit to, restated plainly, with one
 * way back to change any of it. Deliberately NOT a second form: re-editing
 * here would mean two sources of truth for the same values, and the one the
 * customer last touched would win by accident rather than by design.
 *
 * The address is shown as it will be printed on the label, not as a list of
 * field names — a wrong postcode is far easier to spot in the shape you are
 * used to reading.
 */
function ReviewPanel({
  email,
  phone,
  address,
  countryName,
  methodLabel,
  scheduledDate,
  isGift,
  giftRecipient,
  giftMessage,
  note,
  onEdit,
}: {
  email: string;
  phone: string;
  address: AddressInput;
  countryName: string;
  methodLabel: string;
  scheduledDate: string;
  isGift: boolean;
  giftRecipient: string;
  giftMessage: string;
  note: string;
  onEdit: () => void;
}) {
  // Same formatter as the confirmation, so review and receipt agree.
  const addressLines = formatAddressLines(address, () => countryName);

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-line pb-3">
        <div className="flex items-center gap-3">
          <span className="h-px w-8 bg-accent/50" aria-hidden="true" />
          <p className="eyebrow !mt-0">Step 2 of 2</p>
        </div>

        <button
          type="button"
          onClick={onEdit}
          className="text-xs tracking-wide text-muted underline-offset-4 transition-colors hover:text-accent hover:underline"
        >
          Edit details
        </button>
      </div>

      <div>
        <h2 className="font-display text-[1.75rem] leading-[1.15] text-ink sm:text-3xl">
          Check everything over
        </h2>

        <p className="mt-3 max-w-md text-[0.9375rem] leading-[1.8] text-muted">
          Nothing is charged yet. You will choose how to pay on the next step.
        </p>
      </div>

      <ReviewBlock title="Contact">
        <p>{email}</p>
        {phone && <p>{phone}</p>}
      </ReviewBlock>

      <ReviewBlock title={isGift ? "Recipient's address" : 'Delivery address'}>
        {addressLines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </ReviewBlock>

      <ReviewBlock title="Delivery">
        <p>{methodLabel}</p>
        {scheduledDate && <p>Requested for {scheduledDate}</p>}
      </ReviewBlock>

      {isGift && (
        <ReviewBlock title="Gift">
          {giftRecipient && <p>For {giftRecipient}</p>}
          {giftMessage && <p className="italic">“{giftMessage}”</p>}
        </ReviewBlock>
      )}

      {note && (
        <ReviewBlock title="Your note">
          <p>{note}</p>
        </ReviewBlock>
      )}
    </div>
  );
}

function ReviewBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="grid gap-2 border-b border-line pb-6 sm:grid-cols-[10rem_1fr] sm:gap-6">
      <h3 className="text-[0.6875rem] uppercase tracking-[0.2em] text-muted">{title}</h3>

      <div className="text-sm leading-[1.9] text-ink">{children}</div>
    </section>
  );
}
